import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import InstallationsApi from '@/api/installation-api'
import { TimeUtil } from '@/helpers/TimeUtil'
import { companyModule, filterModule } from '@/store/modules/store-accessor'
import { resolveSideviewTabName } from '@/helpers/installationSideviewTab'
import _ from 'lodash';
import { jobItemUnitInfo, itemUnitAbbreviation, itemUnitInfoLabel, deepTrim, warrantyPayloadOption, warrantyValueToMonths } from "@/helpers/data";
import dayjs from 'dayjs';

const sideviewExportBlobType = (format: string) => {
  switch (format) {
    case 'excel':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'zip':
      return 'application/zip';
    default:
      return 'application/pdf';
  }
};

@Module({ namespaced: true, name: "installations" })
export default class InstallationModule extends VuexModule {
  public installations: InstallationDto[] = [];
  public installationsById: InstallationDto[] = [];
  public installationsTable: InstallationTable[] = [];
  public currentInstallation: InstallationDetailsDto =
    {} as InstallationDetailsDto;
  public installationsCount = 0;
  public jobs: Array<any> = [];
  public installationNextTagId = 0;

  public installationInfo: string;
  public installationItems: string;
  public installationJobs: string;
  public installationPhotos: string;
  public installationTypeCombo: string;
  public itemTypeCombo: string;
  public fuelTypeCombo: string;
  public actionTypeCombo: string;
  public isLoadingSideView = true;
  public isLoadingTable = true;
  public params = new URLSearchParams();
  public filtersParams = new URLSearchParams();
  public search = '';
  public filters = "";
  public queryObject: any = {};
  public newInstallationCoordinates: Array<string> = [];
  public installationFilesDetails: InstallationFilesDetailsDto = {
    total_used_space_mb: 0, remaining_available_space_mb: 0, remaining_available_space_bytes: 0, maximum_free_space_mb: 0,
    maximum_free_space_bytes: 0
  };

  public installationUnfinishedJobs: InstallationUnfinishedJobsDto[] = [];
  public installationOffers: OfferDto[] = [];
  public installationOfferDetails: OfferDetailsDto | null = null;
  public installationDetailsInfo: any = {};
  public sideviewTableIsLoading = true;
  public invalidatedInstallationTabs: string[] = [];
  /** Tracks which installation the sideview store currently belongs to (guards stale merges). */
  public activeInstallationId: string | null = null;
  /** Tabs whose data was loaded by prepareInstallationSideview before the sideview opened. */
  public preparedSideviewTabs: string[] = [];

  @Action({ commit: "setInstallations" })
  async getInstallationsAction(query: string): Promise<InstallationDto[]> {
    const installationsApi = new InstallationsApi();
    this.setIsLoadingTable(true);
    const installations = await installationsApi.getAllInstallations(query);
    await installationsApi.getAllInstallationsCount();
    await filterModule.getInstallationsFilters();
    this.setIsLoadingTable(false);

    return installations;
  }

  @Action({ commit: "setInstallationsById" })
  async getInstallationsByIdAction(clientId: string): Promise<InstallationDto[]> {
    const installationsApi = new InstallationsApi();

    const installations = await installationsApi.getAllInstallationsById(clientId);

    return installations;
  }

  @Action({ commit: "setInstallationsCount" })
  async getInstallationsCount(): Promise<CountDto[]> {

    const installationsApi = new InstallationsApi();
    const installactionsCount =
    await installationsApi.getAllInstallationsCount();
    return installactionsCount;
  }

  @Action
  async getInstallationInfo(
    currentInstallationId: string
  ): Promise<InstallationDetailsDto> {
    const query = `?timezone=${dayjs.tz.guess()}`;
    const installationsApi = new InstallationsApi();
    const installationDetails = await installationsApi.getInstallation(
      parseInt(currentInstallationId), query
    );
    this.setCurrentInstallationFor({
      installationId: currentInstallationId,
      data: installationDetails,
    });
    return installationDetails;
  }

  @Action({ commit: "setInstallationNextTagId" })
  async getInstallationNextTagId(): Promise<InstallationNextTagIdDto> {
    const installationsApi = new InstallationsApi();
    const installationNextTagId = await installationsApi.getInstallationNextTagId();
    return installationNextTagId;
  }

  @Action
  async deleteInstallation(data: Record<string, any>) {

    const installationsApi = new InstallationsApi()
    await installationsApi.deleteInstallation(data.id)
    await this.getInstallationsAction(this.filters);
  }

  @Action
  async getInstallationPDF(currentInstallationId: string) {
    const installationsApi = new InstallationsApi();
    const response = await installationsApi.getInstallationPDF(parseInt(currentInstallationId));

    const blob = new Blob([response], { type: "application/pdf" });
    return blob
  }

  @Action
  async deleteInstallationItem(item: Record<string, any>) {
    
    const installationsApi = new InstallationsApi()
    await installationsApi.deleteInstallationItem(item.installation_id, item.id)
    await this.getInstallationDetailsItems(item.installation_id);
    await this.invalidateInstallationTab({tabs: ['tasks'], installationId: item.installation_id});
    await this.getInstallationsAction(this.filters);
  }

  @Action
  async updateInstallationItem(data: Record<string, any>) {

    if (data.cost == '') {
      delete data.cost
    }
    if (!data.installed_at) {
      delete data.installed_at
    }
    if (data.floor == "") {
      delete data.floor
    }
    if (data.notes == "") {
      delete data.notes
    }
    if (data.serial_number == "") {
      data.serial_number = null;
    }
    if (data.notes == "") {
      delete data.notes
    }
    if (data.warranty == "" || data.warranty == null) {
      delete data.warranty
      delete data.warranty_option
    } else {
      data.warranty_option = warrantyPayloadOption(data.warranty, data.warranty_option);
      data.warranty = warrantyValueToMonths(data.warranty, data.warranty_option);
    }
    if (!data.fuel_type_id) {
      data.fuel_type_id = null;
    } else {
      data.fuel_type_id = data.fuel_type_id.id;
    }
    if (!data.installation_placement_type_id) {
      data.installation_placement_type_id = null;
    }
    else {
      data.installation_placement_type_id = data.installation_placement_type_id.id;
    }

    data.item_type_id = data.selected_item.item_type_id;
    data.make = data.selected_item.make;
    data.model = data.selected_item.model;
    delete data.selected_item;

    const installationsApi = new InstallationsApi()

    data = deepTrim(data);
    await installationsApi.updateInstallationItem(data.installation_id, data.id, data)
    await this.getInstallationDetailsItems(data.installation_id);
    await this.invalidateInstallationTab({tabs: ['tasks'], installationId: data.installation_id});
    await this.getInstallationsAction(this.filters);
  }

  @Action
  async addInstallationItem(data: Record<string, any>) {
    data = deepTrim(data);
    let installation_item_data =_.cloneDeep(data);

    if (installation_item_data.cost == '') {
      delete installation_item_data.cost
    }
    if (installation_item_data.installed_at == "") {
      delete installation_item_data.installed_at
    }
    if (installation_item_data.floor == "") {
      delete installation_item_data.floor
    }
    if (installation_item_data.notes == "") {
      delete installation_item_data.notes
    }
    if (installation_item_data.serial_number == "") {
      delete installation_item_data.serial_number
    }
    if (installation_item_data.notes == "") {
      delete installation_item_data.notes
    }
    if (installation_item_data.warranty == "" || installation_item_data.warranty == null) {
      delete installation_item_data.warranty
      delete installation_item_data.warranty_option
    } else {
      installation_item_data.warranty_option = warrantyPayloadOption(installation_item_data.warranty, installation_item_data.warranty_option);
      installation_item_data.warranty = warrantyValueToMonths(installation_item_data.warranty, installation_item_data.warranty_option);
    }
    if (!installation_item_data.fuel_type_id) {
      delete installation_item_data.fuel_type_id;
    } else {
      installation_item_data.fuel_type_id = installation_item_data.fuel_type_id.id;
    }
    if (!installation_item_data.installation_placement_type_id) {
      delete installation_item_data.installation_placement_type_id
    }
    else {
      installation_item_data.installation_placement_type_id = installation_item_data.installation_placement_type_id.id;
    }

    installation_item_data.item_type_id = installation_item_data.selected_item.item_type_id;
    installation_item_data.make = installation_item_data.selected_item.make;
    installation_item_data.model = installation_item_data.selected_item.model;
    delete installation_item_data.selected_item;

    const installationsApi = new InstallationsApi()

    installation_item_data = deepTrim(installation_item_data);
    await installationsApi.createInstallationItem(installation_item_data.installation_id, installation_item_data)
    await this.getInstallationDetailsItems(installation_item_data.installation_id);
    await this.invalidateInstallationTab({tabs: ['tasks'], installationId: installation_item_data.installation_id});
    await this.getInstallationsAction(this.filters);
  }

  @Action
  async addInstallation(data: Record<string, any>) {
    data = deepTrim(data);
    let installation_data = _.cloneDeep(data);

    installation_data.client_id = installation_data.client.id;
    delete installation_data.client

    if (installation_data.address == null || installation_data.address == '') {
      delete installation_data.address
    }
    else {
      installation_data.address = installation_data.address.split(",")[0];
    }
    if (installation_data.zip_code == "") {
      delete installation_data.zip_code
    }
    if (installation_data.floor == "") {
      delete installation_data.floor
    }
    if (installation_data.latitude == "") {
      delete installation_data.latitude
    }
    if (installation_data.longitude == "") {
      delete installation_data.longitude
    }
    if (installation_data.notes == "") {
      delete installation_data.notes
    }
    if (installation_data.owner === null || installation_data.owner.name == "") {
      delete installation_data.owner
    }
    else {
      installation_data.owner_id = installation_data.owner.id;
    }

    installation_data.tag_id = companyModule.company.key + "_" + data.tag;
    delete installation_data.tag

    if (!installation_data.selected_item.item_type_id) {
      delete installation_data.selected_item;
    }
    else {
      installation_data.installation_items = [installation_data.selected_item];
      delete installation_data.selected_item;
    }

    const installationsApi = new InstallationsApi()

    installation_data = deepTrim(installation_data);
    const response = await installationsApi.createInstallation(installation_data)
    await this.getInstallationsAction(this.filters);
    return response;
  }

  @Action
  async editInstallation(data: Record<string, any>) {
    data = deepTrim(data);
    data.client_id = data.client.id;
    delete data.client

    if (data.address == null) {
      delete data.address
    }
    else {
      data.address = data.address.split(",")[0];
    }
    if (data.latitude == "") {
      delete data.latitude
    }
    if (data.longitude == "") {
      delete data.longitude
    }
    
    if (data.owner == null) {
      data.owner_id = null;
    }
    else if (data.owner.id == null) {
      data.owner_id = null;
    }
    else {
      data.owner_id = data.owner.id;
    }

    data.tag_id = companyModule.company.key + "_" + data.tag;
    delete data.tag

    const installationsApi = new InstallationsApi()

    data = deepTrim(data);
    await installationsApi.updateInstallation(data.id, data)

    if (data.view == "installations") {
      await this.getInstallationsAction(this.filters);
    } else {
      this.setIsLoadingTable(true);
      this.getInstallationDetailsInfo(data.id);
      await this.getInstallationsAction(this.filters);
      this.setIsLoadingTable(false);
    }
  }

  @Action
  async editInstallationPhoto(data: Record<string, any>) {
    data = deepTrim(data);
    const installationsApi = new InstallationsApi()
    await installationsApi.updateInstallationPhoto(data.installationId, data.photoId, data)
  }

  @Action
  async editInstallationFile(data: Record<string, any>) {
    data = deepTrim(data);
    const installationsApi = new InstallationsApi()
    await installationsApi.updateInstallationFile(data.installationId, data.fileId, data)
  }

  @Action
  async addInstallationPhoto({installationId, photo} : {installationId: string, photo: any}) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.createInstallationPhoto(installationId, photo)
    return response;
  }

  @Action
  async addInstallationFile({installationId, file} : {installationId: string, file: any}) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.createInstallationFile(installationId, file)
    return response;
  }

  @Action
  async getInstallationFile({installationId, fileId} : {installationId: string, fileId: string}) {
      const installationsApi = new InstallationsApi();
      const response = await installationsApi.getInstallationFile(installationId, fileId);
      const blob = new Blob([response], { type: "application/file" });
      return blob
  }

  @Action
  async getInstallationPhoto({installationId, photoId} : {installationId: string, photoId: string}) {
      const installationsApi = new InstallationsApi();
      const response = await installationsApi.getInstallationPhoto(installationId, photoId);
      const blob = new Blob([response], { type: "image/*" });
      return blob
  }

  @Action
  async deleteInstallationFile({installationId, fileId} : {installationId: string, fileId: string}) {
    const installationsApi = new InstallationsApi()
    await installationsApi.deleteInstallationFile(installationId, fileId);
  }

  @Action
  async deleteInstallationPhoto({installationId, photoId} : {installationId: string, photoId: string}) {
    const installationsApi = new InstallationsApi()
    await installationsApi.deleteInstallationPhoto(installationId, photoId);
  }

  @Action
  async getInstallationItemPdfExportAction(data: Record<string, any>) {
    const installationsApi = new InstallationsApi();
    const response = await installationsApi.getInstallationItemExport(data);
    const blob = new Blob([response], { type: 'application/pdf' });
    return blob;
  }

  @Action({ commit: 'setInstallationUnfinishedJobs' })
  async getInstallationUnfinishedJobs(installation_id: number): Promise<InstallationUnfinishedJobsDto[]> {
    const installationsApi = new InstallationsApi();
    const installationUnfinishedJobs = await installationsApi.getInstallationUnfinishedJobs(installation_id);

    return installationUnfinishedJobs;
  }

  @Action({ commit: 'setInstallationOffers' })
  async getInstallationOffers(installationId: number): Promise<OfferDto[]> {
    const installationsApi = new InstallationsApi();
    const installationOffers = await installationsApi.getInstallationOffers(installationId);

    return installationOffers;
  }

  @Action({ commit: 'setInstallationOfferDetails' })
  async getInstallationOfferDetails(data: Record<string, any>): Promise<OfferDetailsDto> {
    const installationsApi = new InstallationsApi();
    const installationOfferDetails = await installationsApi.getInstallationOfferDetails(data);

    return installationOfferDetails;
  }

  @Action
  async deleteInstallationOffer(data: OfferDto) {

    const installationsApi = new InstallationsApi();
    await installationsApi.deleteInstallationOffer(data);

    await this.getInstallationOffers(Number(data.installation_id));
    await this.makeInstallationSideviewCommonCalls(String(data.installation_id));
  }

  @Action
  async updateInstallationOffer(data: Partial<OfferDto>) {
    if (Object.prototype.hasOwnProperty.call(data, 'assignee_id')) {
      if (typeof data.assignee_id === 'object' && data.assignee_id && Object.values(data.assignee_id).length > 0) {
        data.assignee_id = data.assignee_id.id;
      } else if (typeof data.assignee_id !== 'string' || data.assignee_id.length === 0) {
        data.assignee_id = null;
      }
    }

    if (Object.prototype.hasOwnProperty.call(data, 'valid_until')) {
      data.valid_until = data.valid_until ? data.valid_until : null;
    }

    if (Object.prototype.hasOwnProperty.call(data, 'notes')) {
      data.notes = data.notes ? data.notes : null;
    }

    if (Object.prototype.hasOwnProperty.call(data, 'internal_notes')) {
      data.internal_notes = data.internal_notes ? data.internal_notes : null;
    }

    const installationsApi = new InstallationsApi()

    data = deepTrim(data);

    await installationsApi.updateInstallationOffer(data);

    if (data.installation_id) {
      await this.getInstallationOffers(Number(data.installation_id));
      await this.getInstallationOfferDetails({ installationId: data.installation_id, offerId: data.id });
      await this.makeInstallationSideviewCommonCalls(String(data.installation_id));
    }
  }

  @Action
  async prepareInstallationSideview({ installationId, tab }: { installationId: string; tab?: string | number }): Promise<void> {
    const tabName = resolveSideviewTabName(tab);

    this.ensureInstallationContext(installationId);
    this.setIsLoadingSideView(true);

    const loadedTabs = ['general'];

    try {
      await this.getInstallationDetailsInfo(installationId);

      await Promise.allSettled([
        this.getInstallationDetailsFinancials(installationId),
        this.getInstallationDetailsStats(installationId),
      ]);

      if (tabName !== 'general') {
        try {
          await this.fetchInstallationSideviewTab({ installationId, tabName });
          loadedTabs.push(tabName);
        } catch {
          // Tab data will load lazily when the tab is visited.
        }
      }

      this.setPreparedSideviewTabs(loadedTabs);
    } catch (error) {
      this.setPreparedSideviewTabs([]);
      this.unsetCurrentInstallation();
      throw error;
    } finally {
      this.setIsLoadingSideView(false);
    }
  }

  @Action
  async fetchInstallationSideviewTab({ installationId, tabName }: { installationId: string; tabName: string }): Promise<void> {
    switch (tabName) {
      case 'offers':
        await this.getInstallationOffers(Number(installationId));
        break;
      case 'jobs':
        await this.getInstallationDetailsJobs(installationId);
        break;
      case 'appointments':
        await this.getInstallationDetailsAppointments(installationId);
        break;
      case 'tasks':
        await this.getInstallationDetailsTasks(installationId);
        break;
      case 'items':
        await this.getInstallationDetailsItems(installationId);
        break;
      case 'photos':
        await this.getInstallationDetailsPhotos(installationId);
        break;
      case 'files':
        await this.getInstallationDetailsFiles(installationId);
        break;
      default:
        break;
    }
  }

  @Action
  async getInstallationDetailsInfo(installation_id: string): Promise<InstallationDto[]> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsInfo = await installationsApi.getInstallationDetailsInfo(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsInfo });
    return installationDetailsInfo;
  }

  @Action
  async getInstallationDetailsFinancials(installation_id: string): Promise<InstallationSideviewFinancialsDto> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsFinancials = await installationsApi.getInstallationDetailsFinancials(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsFinancials });
    return installationDetailsFinancials;
  }

  @Action
  async getInstallationDetailsJobs(installation_id: string): Promise<JobDto[]> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsJobs = await installationsApi.getInstallationDetailsJobs(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsJobs });
    return installationDetailsJobs;
  }

  @Action
  async getInstallationDetailsAppointments(installation_id: string): Promise<AppointmentDto[]> {
    this.setSideviewTableIsLoading(true);
    const query = `?timezone=${dayjs.tz.guess()}`;
    const installationsApi = new InstallationsApi();
    const installationDetailsAppointments = await installationsApi.getInstallationDetailsAppointments(installation_id, query);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsAppointments });
    return installationDetailsAppointments;
  }

  @Action
  async getInstallationDetailsTasks(installation_id: string): Promise<TaskDto[]> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsTasks = await installationsApi.getInstallationDetailsTasks(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsTasks });
    return installationDetailsTasks;
  }

  @Action
  async getInstallationDetailsItems(installation_id: string): Promise<ItemDto[]> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsItems = await installationsApi.getInstallationDetailsItems(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsItems });
    return installationDetailsItems;
  }

  @Action
  async getInstallationDetailsPhotos(installation_id: string): Promise<InstallationSideviewPhotoDto[]> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsPhotos = await installationsApi.getInstallationDetailsPhotos(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsPhotos });
    return installationDetailsPhotos;
  }

  @Action
  async getInstallationDetailsStats(installation_id: string): Promise<InstallationSideviewStatsDto> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsStats = await installationsApi.getInstallationDetailsStats(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsStats });
    return installationDetailsStats;
  }

  @Action
  async getInstallationDetailsFiles(installation_id: string): Promise<InstallationSideviewFileDto[]> {
    this.setSideviewTableIsLoading(true);
    const installationsApi = new InstallationsApi();
    const installationDetailsFiles = await installationsApi.getInstallationDetailsFiles(installation_id);
    this.setInstallationsDetailsFor({ installationId: installation_id, data: installationDetailsFiles });
    return installationDetailsFiles;
  }

  @Action
  async makeInstallationSideviewCommonCalls(installationId: string): Promise<void> {
    await Promise.all([
      this.getInstallationDetailsStats(installationId),
      this.getInstallationDetailsFinancials(installationId),
    ]);
  }

  @Action
  async invalidateInstallationTab({tabs, installationId}: {tabs: string | string[]; installationId: string}): Promise<void> {
    if (this.activeInstallationId != null && String(installationId) !== this.activeInstallationId) {
      return;
    }

    if (this.activeInstallationId != null) {
      this.setInvalidatedInstallationTabs(tabs);
    }

    await this.makeInstallationSideviewCommonCalls(installationId);
  }

  @Action
  async getInstallationSideviewAppointmentsExport(data: Record<string, any>) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.getInstallationSideviewAppointmentsExport(data);
    const blob = new Blob([response], { type: sideviewExportBlobType(data.format) });
    return blob;
  }

  @Action
  async getInstallationSideviewTasksExport(data: Record<string, any>) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.getInstallationSideviewTasksExport(data);
    const blob = new Blob([response], { type: sideviewExportBlobType(data.format) });
    return blob;
  }

  @Action
  async getInstallationSideviewJobsExport(data: Record<string, any>) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.getInstallationSideviewJobsExport(data);
    const blob = new Blob([response], { type: sideviewExportBlobType(data.format) });
    return blob;
  }

  @Action
  async getInstallationSideviewItemsExport(data: Record<string, any>) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.getInstallationSideviewItemsExport(data);
    const blob = new Blob([response], { type: sideviewExportBlobType(data.format) });
    return blob;
  }

  @Action
  async getInstallationSideviewOffersExport(data: Record<string, any>) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.getInstallationSideviewOffersExport(data);
    const blob = new Blob([response], { type: sideviewExportBlobType(data.format) });
    return blob;
  }

  @Action
  async getInstallationSideviewPhotosExport(data: Record<string, any>) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.getInstallationSideviewPhotosExport(data);
    const blob = new Blob([response], { type: sideviewExportBlobType('zip') });
    return blob;
  }

  @Action
  async getInstallationSideviewFilesExport(data: Record<string, any>) {
    const installationsApi = new InstallationsApi()
    const response = await installationsApi.getInstallationSideviewFilesExport(data);
    const blob = new Blob([response], { type: sideviewExportBlobType('zip') });
    return blob;
  }

  @Mutation
  public setInstallations (installations: InstallationDto[]): void {
    try {
      installations.map(item => {
        item.client_name = `${item.client_lastname} ${item.client_firstname}`
        item.tag_split = item.tag_id.split('_').pop();

        // installation items description - start
        const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;
        if(installationItemDescriptionIsEnabled) {
          const types = item.primary_item_types;
          const descriptions = item.primary_items_descriptions;

          if (Array.isArray(types) && Array.isArray(descriptions)) {
            const length = Math.min(types.length, descriptions.length);
            item.primary_item_types = [];
  
            for (let i = 0; i < length; i++) {
              item.primary_item_types.push(types[i] ? `${descriptions[i] ?? ''}${descriptions[i] ? ' | ' : ''}${types[i]}` : '-')
            }
          }
        }
        // installation items description - end

      })
      this.installations = installations
      this.isLoadingTable = false;
    } catch(error) {
      this.isLoadingTable = false;
    }
  }

  @Mutation
  public clearInstallations(): void {
    this.installations = [];
  }

  @Mutation
  public setInstallationsCount(installationCount: CountDto): void {
    this.installationsCount = installationCount.count;
  }

  @Mutation
  public setInstallationNextTagId(installationNextTagId: InstallationNextTagIdDto): void {
    this.installationNextTagId = installationNextTagId.tag_id;
  }

  @Mutation
  public ensureInstallationContext(installationId: string | number): void {
    const nextId = String(installationId);
    if (this.activeInstallationId !== nextId) {
      this.activeInstallationId = nextId;
      this.currentInstallation = {} as InstallationDetailsDto;
      this.invalidatedInstallationTabs = [];
      this.preparedSideviewTabs = [];
    }
  }

  @Mutation
  public setCurrentInstallation(installationInfo: InstallationDetailsDto): void {
    this.currentInstallation = installationInfo;
    this.isLoadingSideView = false;
  }

  @Mutation
  public setCurrentInstallationFor({ installationId, data }: { installationId: string | number; data: InstallationDetailsDto }): void {
    if (this.activeInstallationId != null && String(installationId) !== this.activeInstallationId) {
      return;
    }
    this.currentInstallation = data;
    this.isLoadingSideView = false;
  }

  @Mutation
  public setInstallationsById (installationsById: InstallationDto[]): void {

    installationsById.map(installation => {
      installation.tag_split = installation.tag_id.split('_').pop();
    })
    this.installationsById = installationsById
  }

  @Mutation
  public unsetCurrentInstallation(): void {
    this.currentInstallation = {} as InstallationDetailsDto;
    this.activeInstallationId = null;
    this.invalidatedInstallationTabs = [];
    this.preparedSideviewTabs = [];
  }

  @Mutation
  public setPreparedSideviewTabs(tabs: string[]): void {
    this.preparedSideviewTabs = tabs;
  }

  get mapJobsAndActions(): InstallationJobList[] {
    if(!this.currentInstallation?.jobs) return [];

    const newJobs = this.currentInstallation?.jobs?.map((job) => {

      let actions = this.currentInstallation.actions?.filter((action) => {
        return action.job_id === job.id;
      });

      let materials = this.currentInstallation.materials?.filter((materials) => {
        return materials.job_id === job.id;
      });

      let payments = this.currentInstallation.payments?.filter((payment) => {
        return payment.job_id === job.id;
      });

      let expenses = this.currentInstallation.expenses?.filter((expense) => {
        return expense.job_id === job.id;
      });

      let appointments = this.currentInstallation.appointments?.filter(
        (appointment) => {
          return appointment.job_id === job.id;
        }
      );

      actions = actions.map((item) => {
        const newItem = item;
        newItem.unitInfo = jobItemUnitInfo(item);
        
        newItem.entry_type = 'action';
        return newItem;
      })

      materials = materials.map((item) => {
        const newItem = item;
        
        newItem.entry_type = 'material';
        newItem.unitInfo = itemUnitInfoLabel(newItem);
        return newItem;
      })

      appointments = appointments?.map((item) => {
        const newItem = item;
        
        newItem.entry_type = 'appointment';
        return newItem;
      })

      payments = payments?.map((item) => {
        const newItem = item;
        
        newItem.entry_type = 'payment';
        return newItem;
      })

      expenses = expenses?.map((item) => {
        const newItem = item;
        
        newItem.entry_type = 'expense';
        return newItem;
      })

      
      const actionsAndPaymentsAndAppointments: Record<string, any>[] = ((actions as []).concat(materials as [])).concat(payments as []).concat(expenses as []);

      actionsAndPaymentsAndAppointments.sort((a, b) => {
        const c: any = new Date(a.created_at);
        const d: any = new Date(b.created_at);
        return c - d;
      });
      
      job.actions = [];
      job.actions = actionsAndPaymentsAndAppointments;

      return job;
    });

    return newJobs?.filter((item) => {
      return (item.actions.length > 0)
    })
  }

  @Mutation
  public setIsLoadingSideView(isLoadingSideView: boolean): void {
    this.isLoadingSideView = isLoadingSideView
  }

  @Mutation
  public setIsLoadingTable(isLoadingTable: boolean): void {
    this.isLoadingTable = isLoadingTable
  }

  @Mutation
  public setParams(params: URLSearchParams): void {
    if(this.search != '') {
      params.set('search', this.search);
    }
    this.params = params
  }

  @Mutation
  public setSearch(search: string): void {
    this.search = search
  }

  @Mutation
  public setFilters(filters: string): void {
    this.filters = filters
  }

  @Mutation
  public setQueryObject(queryObject: any): void {
    this.queryObject = queryObject
  }

  @Mutation
  public setNewInstallationCoordinates(newInstallationCoordinates: any): void {
    this.newInstallationCoordinates = newInstallationCoordinates;
  }

  @Mutation
  public clearNewInstallationCoordinates(): void {
    this.newInstallationCoordinates = [];
  }

  @Mutation
  public setInstallationFilesDetails(installationFilesDetails: InstallationFilesDetailsDto): void {
    this.installationFilesDetails = installationFilesDetails;
  }

  @Mutation
  public setInstallationUnfinishedJobs(installationUnfinishedJobs: InstallationUnfinishedJobsDto[]): void {
    this.installationUnfinishedJobs = installationUnfinishedJobs.map(i => ({
      ...i,
      job_text: i.description ?  `${i.description} | ${TimeUtil.formatDateTime(i.last_job_entry_at)} |`
      : `${i.actions.join(', ')} | ${TimeUtil.formatDateTime(i.last_job_entry_at)} |`
    }));
  }

  @Mutation
  public clearInstallationUnfinishedJobs(): void {
    this.installationUnfinishedJobs = [];
  }

  @Mutation
  public setInstallationOffers(installationOffers: OfferDto[]): void {
    this.installationOffers = installationOffers;
  }

  @Mutation
  public setInstallationOfferDetails(installationOfferDetails: OfferDetailsDto | null): void {
    this.installationOfferDetails = installationOfferDetails;
  }

  @Mutation
  public setCurrentInstallationDetailsInfo(installationDetailsInfo: any): void {
    this.currentInstallation = {...this.currentInstallation, ...installationDetailsInfo};
    this.isLoadingSideView = false;
    this.sideviewTableIsLoading = false;
  }

  @Mutation
  public setCurrentInstallationDetailsJobs(installationDetailsInfo: any): void {
    this.currentInstallation = {...this.currentInstallation, ...installationDetailsInfo};

    this.isLoadingSideView = false;
    this.sideviewTableIsLoading = false;
  }

  @Mutation
  public setInstallationsDetails(installationDetailsObj: any): void {
    this.currentInstallation = {...this.currentInstallation, ...installationDetailsObj};

    this.isLoadingSideView = false;
    this.sideviewTableIsLoading = false;
  }

  @Mutation
  public setInstallationsDetailsFor({ installationId, data }: { installationId: string | number; data: any }): void {
    if (this.activeInstallationId != null && String(installationId) !== this.activeInstallationId) {
      this.sideviewTableIsLoading = false;
      return;
    }
    this.currentInstallation = {...this.currentInstallation, ...data};
    this.isLoadingSideView = false;
    this.sideviewTableIsLoading = false;
  }

  @Mutation
  public setSideviewTableIsLoading(isLoading: boolean): void {
    this.sideviewTableIsLoading = isLoading;
  }

  @Mutation
  public setInvalidatedInstallationTabs(tabs: string | string[]): void {
    const tabsToInvalidate = Array.isArray(tabs) ? tabs : [tabs];
    this.invalidatedInstallationTabs = Array.from(new Set([...this.invalidatedInstallationTabs, ...tabsToInvalidate]));
  }

  @Mutation
  public validateInstallationTab(tab: string): void {
    this.invalidatedInstallationTabs = this.invalidatedInstallationTabs.filter(invalidatedTab => invalidatedTab !== tab);
  }

}
